// @flow
import FileSaver from 'file-saver';
import {
  get,
  post,
  put,
  patch,
  del,
  apiURL,
  getRequestOptions,
} from '../utils/api';
import { withVoucherTracking } from '../utils/hudVoucherTracking';
import { currentStatusSink } from '../utils/requestStatusSink';

const buildQueryString = (params: Object = {}): string => {
  const cleaned: { [string]: string } = Object.entries(params).reduce(
    (acc: { [string]: string }, [key, value]) => {
      return !!value ? { ...acc, [key]: String(value) } : acc;
    },
    {},
  );
  const qs = new URLSearchParams(cleaned).toString();
  return qs ? `?${qs}` : '';
};

export const getHudVouchers = ({
  organizationId,
  propertyId,
  params = {},
}: {
  organizationId: string,
  propertyId: string,
  params?: Object,
}): Promise<any> => {
  return get(
    `/${organizationId}/${propertyId}/hud-vouchers${buildQueryString(params)}`,
  );
};

export type HudVoucherSetup = {
  isConfigured: boolean,
  voucherSetupYearMonth: string | null,
};

export const getHudVoucherSetup = ({
  organizationId,
  propertyId,
}: {
  organizationId: string,
  propertyId: string,
}): Promise<HudVoucherSetup> => {
  return get(`/${organizationId}/${propertyId}/hud-vouchers/setup`);
};

export const startHudVoucherDraftAutomation = ({
  organizationId,
  propertyId,
  beginDate,
}: {
  organizationId: string,
  propertyId: string,
  beginDate: string,
}): Promise<any> => {
  const body = JSON.stringify({ beginDate });
  return withVoucherTracking('draft_started', 'POST /hud-vouchers/drafts', () =>
    post(`/${organizationId}/${propertyId}/hud-vouchers/drafts`, body),
  );
};

export const updateHudVoucherBeginDate = ({
  organizationId,
  propertyId,
  beginDate,
}: {
  organizationId: string,
  propertyId: string,
  beginDate: string,
}): Promise<any> => {
  const body = JSON.stringify({ beginDate });
  return withVoucherTracking(
    'begin_date_updated',
    'PUT /hud-vouchers/drafts/begin-date',
    () =>
      put(
        `/${organizationId}/${propertyId}/hud-vouchers/drafts/begin-date`,
        body,
      ),
  );
};

export const undoHudVoucherSetup = ({
  organizationId,
  propertyId,
}: {
  organizationId: string,
  propertyId: string,
}): Promise<any> => {
  return withVoucherTracking(
    'setup_undone',
    'DELETE /hud-vouchers/drafts',
    () => del(`/${organizationId}/${propertyId}/hud-vouchers/drafts`),
  );
};

export const getHudVoucherSpecialClaims = ({
  organizationId,
  propertyId,
  voucherId,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
}): Promise<any> => {
  return get(
    `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/special-claims`,
  );
};

export const createHudVoucherSpecialClaim = ({
  organizationId,
  propertyId,
  voucherId,
  payload,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
  payload: {
    hudClaimId: string,
    claimType: string,
    requestedAmount: number,
    propertyTransactionCodeId: string,
  },
}): Promise<any> => {
  return withVoucherTracking(
    'special_claim_created',
    'POST /hud-vouchers/{voucherId}/special-claims',
    () =>
      post(
        `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/special-claims`,
        JSON.stringify(payload),
      ),
  );
};

export const getHudVoucherMiscAccountingRecords = ({
  organizationId,
  propertyId,
  voucherId,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
}): Promise<any> => {
  return get(
    `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/misc-accounting`,
  );
};

export const getHudVoucherAdjustments = ({
  organizationId,
  propertyId,
  voucherId,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
}): Promise<any> => {
  return get(
    `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/adjustments`,
  );
};

export const createHudVoucherMiscAccountingRecord = ({
  organizationId,
  propertyId,
  voucherId,
  payload,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
  payload: {
    requestType: string,
    comments: string,
    requestedAmount: number,
    propertyTransactionCodeId: string,
  },
}): Promise<any> => {
  return withVoucherTracking(
    'misc_accounting_created',
    'POST /hud-vouchers/{voucherId}/misc-accounting',
    () =>
      post(
        `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/misc-accounting`,
        JSON.stringify(payload),
      ),
  );
};

export const deleteHudVoucherMiscAccountingRecord = ({
  organizationId,
  propertyId,
  voucherId,
  miscAccountingId,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
  miscAccountingId: string,
}): Promise<any> => {
  return withVoucherTracking(
    'misc_accounting_deleted',
    'DELETE /hud-vouchers/{voucherId}/misc-accounting/{miscAccountingId}',
    () =>
      del(
        `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/misc-accounting/${miscAccountingId}`,
      ),
  );
};

export const updateHudVoucherSpecialClaimApprovedAmount = ({
  organizationId,
  propertyId,
  voucherId,
  specialClaimId,
  approvedAmount,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
  specialClaimId: string,
  approvedAmount: number,
}): Promise<any> => {
  return withVoucherTracking(
    'special_claim_updated',
    'PATCH /hud-vouchers/{voucherId}/special-claims/{specialClaimId}',
    () =>
      patch(
        `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/special-claims/${specialClaimId}`,
        JSON.stringify({ approvedAmount }),
      ),
  );
};

export const updateHudVoucherMiscAccountingApprovedAmount = ({
  organizationId,
  propertyId,
  voucherId,
  miscAccountingId,
  approvedAmount,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
  miscAccountingId: string,
  approvedAmount: number,
}): Promise<any> => {
  return withVoucherTracking(
    'misc_accounting_updated',
    'PATCH /hud-vouchers/{voucherId}/misc-accounting/{miscAccountingId}',
    () =>
      patch(
        `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/misc-accounting/${miscAccountingId}`,
        JSON.stringify({ approvedAmount }),
      ),
  );
};

export const updateHudVoucherRegularAssistanceApprovedAmount = ({
  organizationId,
  propertyId,
  voucherId,
  regularAssistancePaymentId,
  approvedAmount,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
  regularAssistancePaymentId: string,
  approvedAmount: number | null,
}): Promise<any> => {
  return withVoucherTracking(
    'regular_assistance_updated',
    'PATCH /hud-vouchers/{voucherId}/regular-assistance/{regularAssistancePaymentId}',
    () =>
      patch(
        `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/regular-assistance/${regularAssistancePaymentId}`,
        JSON.stringify({ approvedAmount }),
      ),
  );
};

export const updateHudVoucherAdjustmentApprovedAmount = ({
  organizationId,
  propertyId,
  voucherId,
  adjustmentId,
  approvedAmount,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
  adjustmentId: string,
  approvedAmount: number | null,
}): Promise<mixed> => {
  return withVoucherTracking(
    'adjustment_amount_updated',
    'PATCH /hud-vouchers/{voucherId}/adjustments/{adjustmentId}',
    () =>
      patch(
        `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/adjustments/${adjustmentId}`,
        JSON.stringify({ approvedAmount }),
      ),
  );
};

const downloadMat30FileUntracked = async ({
  organizationId,
  propertyId,
  voucherId,
}: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
}): Promise<Blob> => {
  // Read before the first await so the status reaches the tracked call that started this download.
  const reportStatus = currentStatusSink();
  const url = `${apiURL}/${organizationId}/${propertyId}/mat30-file?voucherId=${voucherId}`;
  const response = await fetch(url, {
    method: 'GET',
    ...getRequestOptions(),
  });

  if (!response.ok) {
    reportStatus?.(response.status);
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || 'Failed to download MAT30 file.');
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="(.+)"/);
  const fileName = match ? match[1] : 'MAT30.txt';

  FileSaver.saveAs(blob, fileName);
  return blob;
};

export const downloadMat30File = (args: {
  organizationId: string,
  propertyId: string,
  voucherId: string,
}): Promise<Blob> =>
  withVoucherTracking('mat30_downloaded', 'GET /mat30-file', () =>
    downloadMat30FileUntracked(args),
  );
